export type ProjectTypeKey = "serial" | "stories" | "commercial" | "short_film"

export type ProjectTypeTermKey = "season" | "episode" | "scene" | "shot" | "seasons_nav"

export type ProjectTypeTerms = Record<ProjectTypeTermKey, { en: string; ru: string }>

export type ProjectTypeConfig = {
  key: ProjectTypeKey
  visible_levels: string[]
  allowed_sections: string[]
  analytics_kpis: string[]
  max_seasons: number | null
  max_episodes_per_season: number | null
  default_frame_format: string
  after_create_redirect: string
  hidden_wrappers: string[]
  show_episode_selector: boolean
  show_pipeline: boolean
  terms: ProjectTypeTerms
}

export type ProjectTypeCard = {
  key: ProjectTypeKey
  icon: "film" | "smartphone" | "megaphone" | "clapperboard"
  structureHintKey:
    | "wizard.projectTypes.serial.structure"
    | "wizard.projectTypes.stories.structure"
    | "wizard.projectTypes.commercial.structure"
    | "wizard.projectTypes.short_film.structure"
}

export const PROJECT_TYPE_KEYS: readonly ProjectTypeKey[] = ["stories", "serial", "commercial", "short_film"] as const

/** Fallback when project_type is missing (e.g. legacy cards). New wizard default is Stories. */
export const DEFAULT_PROJECT_TYPE: ProjectTypeKey = "serial"

export const WIZARD_DEFAULT_PROJECT_TYPE: ProjectTypeKey = "stories"

export const PROJECT_TYPE_CARDS: readonly ProjectTypeCard[] = [
  {
    key: "stories",
    icon: "smartphone",
    structureHintKey: "wizard.projectTypes.stories.structure",
  },
  {
    key: "serial",
    icon: "film",
    structureHintKey: "wizard.projectTypes.serial.structure",
  },
  {
    key: "commercial",
    icon: "megaphone",
    structureHintKey: "wizard.projectTypes.commercial.structure",
  },
  {
    key: "short_film",
    icon: "clapperboard",
    structureHintKey: "wizard.projectTypes.short_film.structure",
  },
] as const

export function isProjectTypeKey(value: unknown): value is ProjectTypeKey {
  return typeof value === "string" && (PROJECT_TYPE_KEYS as readonly string[]).includes(value)
}

export function projectTypeKey(type: string | null | undefined): ProjectTypeKey {
  return isProjectTypeKey(type) ? type : DEFAULT_PROJECT_TYPE
}

export function projectSectionAllowed(
  typeConfig: Pick<ProjectTypeConfig, "allowed_sections"> | undefined,
  section: string,
): boolean {
  return typeConfig?.allowed_sections?.includes(section) ?? false
}

/**
 * Секции бокового меню. Суперадмин дополнительно видит скрытую «Студию».
 * Пустой список не дополняем: фильтр и так показывает все пункты.
 */
export function sectionsVisibleToUser(
  allowedSections: readonly string[] | undefined,
  superAdmin: boolean,
): readonly string[] | undefined {
  if (!superAdmin || !allowedSections || allowedSections.length === 0) return allowedSections
  if (allowedSections.includes("studio")) return allowedSections
  return [...allowedSections, "studio"]
}

function navSectionVisible(href: string, allowed: ReadonlySet<string>): boolean {
  if (href === "studio") return allowed.has("studio") || allowed.has("assembly")
  return allowed.has(href)
}

export function filterNavGroupsBySections<T extends { items: readonly { href: string }[] }>(
  groups: readonly T[],
  allowedSections: readonly string[] | undefined,
): T[] {
  if (!allowedSections || allowedSections.length === 0) return [...groups]
  const allowed = new Set(allowedSections)
  return groups
    .map((group) => ({
      ...group,
      items: group.items.filter((item) => navSectionVisible(item.href, allowed)),
    }))
    .filter((group) => group.items.length > 0) as T[]
}

export function projectTypeLabel(
  terms: ProjectTypeTerms | undefined,
  key: ProjectTypeTermKey,
  locale: string,
): string | undefined {
  if (!terms?.[key]) return undefined
  return locale.startsWith("ru") ? terms[key].ru : terms[key].en
}
